import { ETabsIcon, ModalType } from "@/enums";
import {
  DeleteModalIcon,
  ErrorIcon,
  FinishingIcon,
  MessageLogsIcon,
  PricingIcon,
  PrintingDetailsIcon,
  SuccessIcon,
  WarningModalIcon,
  WarningLogsIcon,
  DuplicateModalIcon,
  EditModalIcon,
  AddModalIcon,
} from "@/icons";
import {
  ApprovalStatus,
  DELIVERY_NOTE_STATUSES,
  DOCUMENT_TYPE,
  PROFORMA_INVOICE_STATUSES,
  QUOTE_STATUSES,
} from "@/pages-components/quotes/enums";
import { ECalculationLogType } from "@/widgets/product-pricing-widget/enums";
import { t } from "i18next";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";

export const _renderActiveIcon = (icon) => {
  if (icon === ETabsIcon.PRINTING_DETAILS) {
    return <PrintingDetailsIcon />;
  }
  if (icon === ETabsIcon.FINISHING) {
    return <FinishingIcon />;
  }
  if (icon === ETabsIcon.PRICING) {
    return <PricingIcon />;
  }
};

export const _renderUnActiveIcon = (icon, primaryColor) => {
  if (icon === ETabsIcon.PRINTING_DETAILS) {
    return <PrintingDetailsIcon stroke={primaryColor(600)} />;
  }
  if (icon === ETabsIcon.FINISHING) {
    return <FinishingIcon stroke={primaryColor(600)} />;
  }
  if (icon === ETabsIcon.PRICING) {
    return <PricingIcon stroke={primaryColor(600)} />;
  }
};

export const compareStrings = (string1, string2) => {
  return string1?.toLowerCase() === string2?.toLowerCase();
};

export const _renderQuoteStatus = (status: number, quote: any, t: any) => {
  if (status === QUOTE_STATUSES.Create) {
    if (quote?.userName === null) {
      return t("sales.quote.create");
    } else {
      return t("sales.quote.createdBy", { name: quote?.userName });
    }
  }
  if (status === QUOTE_STATUSES.Open) {
    return t("sales.quote.open");
  }
  if (status === QUOTE_STATUSES.Closed) {
    return t("sales.quote.closed");
  }
  if (status === QUOTE_STATUSES.Canceled) {
    return t("sales.quote.canceled");
  }
  if (status === QUOTE_STATUSES.Waiting) {
    return t("sales.quote.waiting");
  }
  if (status === QUOTE_STATUSES.Approved) {
    return t("sales.quote.approved");
  }
  if (status === QUOTE_STATUSES.CanceledIrrelvant) {
    return t("sales.quote.canceledIrrelvant");
  }
  if (status === QUOTE_STATUSES.CanceledPrice) {
    return t("sales.quote.canceledPrice");
  }
  if (status === QUOTE_STATUSES.CanceledDeliveryTime) {
    return t("sales.quote.canceledDeliveryTime");
  }
  if (status === QUOTE_STATUSES.CanceledDuplicate) {
    return t("QuoteStatuses.CANCELED_DUPLICATE");
  }
  if (status === QUOTE_STATUSES.CanceledOther) {
    return t("sales.quote.canceledOther");
  }
  if (status === QUOTE_STATUSES.ApprovedByManager) {
    return t("sales.quote.approvedByManager");
  }
  if (status === QUOTE_STATUSES.RejectedByManager) {
    return t("sales.quote.rejectedByManager");
  }
  if (status === QUOTE_STATUSES.PartialClosed) {
    return t("sales.quote.partialClosed");
  }
  if (status === QUOTE_STATUSES.WaitForPrintHouseConfirm) {
    return t("sales.quote.waitForPrintHouseConfirm");
  }
};

// for receipts / delivery note / delivery note refund
export const _renderDocumentStatus = (status: number, t: any) => {
  if (status === DELIVERY_NOTE_STATUSES.Open) {
    return t("sales.quote.open");
  }
  if (status === DELIVERY_NOTE_STATUSES.Canceled) {
    return t("sales.quote.canceled");
  }
  if (status === DELIVERY_NOTE_STATUSES.Created) {
    return t("sales.quote.created");
  }
  if (status === DELIVERY_NOTE_STATUSES.Refunded) {
    return t("sales.quote.refunded");
  }
  if (status === DELIVERY_NOTE_STATUSES.Confirmed) {
    return t("sales.quote.confirmed");
  }
  if (status === DELIVERY_NOTE_STATUSES.Rejected) {
    return t("sales.quote.rejected");
  }
  if (status === DELIVERY_NOTE_STATUSES.PartialRefunded) {
    return t("sales.quote.partialRefunded");
  }
  if (status === DELIVERY_NOTE_STATUSES.ClosedAsInvoice) {
    return t("sales.quote.closedAsInvoice");
  }
  if (status === DELIVERY_NOTE_STATUSES.ClosedByMultiDocuments) {
    return t("sales.quote.closedByMultiDocuments");
  }
  if (status === DELIVERY_NOTE_STATUSES.ManualClose) {
    return t("sales.quote.manualClose");
  }
};

// for proforma invoice
export const _renderProformaInvoiceStatus = (status: number, t: any) => {
  if (status === PROFORMA_INVOICE_STATUSES.Open) {
    return t("sales.quote.open");
  }
  if (status === PROFORMA_INVOICE_STATUSES.Canceled) {
    return t("sales.quote.canceled");
  }
  if (status === PROFORMA_INVOICE_STATUSES.Created) {
    return t("sales.quote.created");
  }
  if (status === PROFORMA_INVOICE_STATUSES.ClosedAsInvoice) {
    return t("sales.quote.closedAsInvoice");
  }
  if (status === PROFORMA_INVOICE_STATUSES.ClosedByMultiDocuments) {
    return t("sales.quote.closedByMultiDocuments");
  }
  if (status === PROFORMA_INVOICE_STATUSES.ManualClose) {
    return t("sales.quote.manualClose");
  }
};

export const _renderStatus = (
  docType: DOCUMENT_TYPE,
  document: any,
  t: any
): JSX.Element => {
  if (document) {
    let statusContent: JSX.Element | null = null;
    const docTypeText =
      DOCUMENT_TYPE[docType].charAt(0).toUpperCase() +
      DOCUMENT_TYPE[docType].slice(1);
    const openStatus = `${docTypeText}.Open`;

    let statusApproval: JSX.Element | null = null;
    if (
      docType === DOCUMENT_TYPE.quote &&
      document &&
      document?.approvalStatus &&
      document.approvalStatus !== null
    ) {
      statusApproval = (
        <>{t(`documentStatus.${ApprovalStatus[document.approvalStatus]}`)}</>
      );
    }

    // Handle status with hyphen (-)
    const statusParts = document?.statusTitleText?.split("-");
    const translatedStatus =
      statusParts?.length > 0 ? t(`documentStatus.${statusParts[0]}`) : "";

    if (
      document?.statusTitleText === openStatus ||
      document?.statusTitleText === "DeliveryNote.Open" ||
      document?.statusTitleText === "Invoice.Open" ||
      document?.statusTitleText === "PurchaseOrder.Open" ||
      document?.statusTitleText === "PurchaseOrder.UnderCreation"
    ) {
      return (
        <div>
          <div style={{ fontWeight: "bold" }}>
            {docType == DOCUMENT_TYPE.purchaseOrder
              ? t("documentStatus.PurchaseOrder.UnderCreation")
              : t("sales.quote.open")}
          </div>
          {statusApproval}
        </div>
      );
    } else if (document?.statusTitleText === "Quote.Create") {
      if (document?.userName === null) {
        return (
          <div>
            <div style={{ fontWeight: "bold" }}>{t("sales.quote.create")}</div>
            {statusApproval}
          </div>
        );
      } else {
        return (
          <div>
            <div style={{ fontWeight: "bold" }}>
              {t("sales.quote.createdBy", { name: document?.userName })}
            </div>
            {statusApproval}
          </div>
        );
      }
    } else if (document.externalDocumentNumber) {
      const externalDocument = document.externalDocumentNumber;
      let result: JSX.Element[] = [];
      if (externalDocument.length > 0) {
        result.push(
          <div key="external-doc">
            {t(`documentStatus.${document.titleExternalDocumentNumber}.title`)}:
            {externalDocument}
          </div>
        );
      }
      statusContent = <>{result}</>;
    } else if (
      (document.documentNumbers && document.documentNumbers.length > 0) ||
      (document.secondDocumentNumbers &&
        document.secondDocumentNumbers.length > 0) ||
      (document.thirdDocumentNumbers &&
        document.thirdDocumentNumbers.length > 0) ||
      (document.fourthDocumentNumbers &&
        document.fourthDocumentNumbers.length > 0)
    ) {
      const firstDocuments = document.documentNumbers
        ? document.documentNumbers.map((item: any, index: number) => (
            <span key={index}>
              <a
                style={{
                  minWidth: "auto",
                  height: "auto",
                  padding: 0,
                  color: "var(--theme-primary-color, #2e3092)",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
                href={`/${
                  document.titleDocumentNumber.charAt(0).toLowerCase() +
                  document.titleDocumentNumber.slice(1)
                }?Id=${item.documentId}`}
              >
                {item.documentNumber}
              </a>
              {index < document.documentNumbers.length - 1 && ", "}
            </span>
          ))
        : [];

      const secondDocuments = document.secondDocumentNumbers
        ? document.secondDocumentNumbers.map((item: any, index: number) => {
            const isPayment = document.titleSecondDocumentNumber === "Payment";
            const href = isPayment
              ? `/receipt?isOutgoingPayments=true&Id=${item.documentId}`
              : `/${
                  document.titleSecondDocumentNumber.charAt(0).toLowerCase() +
                  document.titleSecondDocumentNumber.slice(1)
                }?Id=${item.documentId}`;

            return (
              <span key={index}>
                <a
                  style={{
                    minWidth: "auto",
                    height: "auto",
                    padding: 0,
                    color: "var(--theme-primary-color, #2e3092)",
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                  href={href}
                >
                  {item.documentNumber}
                </a>
                {index < document.secondDocumentNumbers.length - 1 && ", "}
              </span>
            );
          })
        : [];

      const thirdDocuments = document.thirdDocumentNumbers
        ? document.thirdDocumentNumbers.map((item: any, index: number) => (
            <span key={`third-${index}`}>
              <a
                style={{
                  minWidth: "auto",
                  height: "auto",
                  padding: 0,
                  color: "var(--theme-primary-color, #2e3092)",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
                href={`/${
                  document.titleThirdDocumentNumber.charAt(0).toLowerCase() +
                  document.titleThirdDocumentNumber.slice(1)
                }?Id=${item.documentId}`}
              >
                {item.documentNumber}
              </a>
              {index < document.thirdDocumentNumbers.length - 1 && ", "}
            </span>
          ))
        : [];

      const fourthDocuments = document.fourthDocumentNumbers
        ? document.fourthDocumentNumbers.map((item: any, index: number) => (
            <span key={`fourth-${index}`}>
              <a
                style={{
                  minWidth: "auto",
                  height: "auto",
                  padding: 0,
                  color: "var(--theme-primary-color, #2e3092)",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
                href={`/${
                  document.titleFourthDocumentNumber.charAt(0).toLowerCase() +
                  document.titleFourthDocumentNumber.slice(1)
                }?Id=${item.documentId}`}
              >
                {item.documentNumber}
              </a>
              {index < document.fourthDocumentNumbers.length - 1 && ", "}
            </span>
          ))
        : [];

      let result: JSX.Element[] = [];

      if (firstDocuments.length > 0) {
        result.push(
          <div key="first-documents">
            {t(`documentStatus.${document.titleDocumentNumber}.title`)}:{" "}
            {firstDocuments}
          </div>
        );
      }

      if (secondDocuments.length > 0) {
        result.push(
          <div key="second-documents">
            {t(`documentStatus.${document.titleSecondDocumentNumber}.title`)}:{" "}
            {secondDocuments}
          </div>
        );
      }

      if (thirdDocuments.length > 0) {
        result.push(
          <div key="third-documents">
            {t(`documentStatus.${document.titleThirdDocumentNumber}.title`)}:{" "}
            {thirdDocuments}
          </div>
        );
      }

      if (fourthDocuments.length > 0) {
        result.push(
          <div
            key="fourth-documents"
            style={{
              color:
                "var(--theme-table-body-color, var(--theme-text-primary, #2e3092))",
            }}
          >
            {t(`documentStatus.${document.titleFourthDocumentNumber}.title`)}:{" "}
            {fourthDocuments}
          </div>
        );
      }

      statusContent = <>{result}</>;
    }

    return (
      <div>
        <div style={{ fontWeight: "bold" }}>
          {translatedStatus}
          {statusParts && statusParts?.length > 1
            ? ` - ${statusParts.slice(1).join("-")}`
            : ""}
        </div>
        {statusContent}
      </div>
    );
  }
  return <></>;
};

export function getParameterByParameterCode(subProductArray, code) {
  for (let i = 0; i < subProductArray.length; i++) {
    const parameters = subProductArray[i].parameters;
    for (let j = 0; j < parameters.length; j++) {
      if (parameters[j].parameterCode === code) {
        return parameters[j];
      }
    }
  }
  return null;
}

export const _renderIconLogs = (type, width, height) => {
  if (type === ECalculationLogType.ERROR) {
    return <ErrorIcon width={width} height={height} />;
  } else if (type === ECalculationLogType.MESSAGE) {
    return <MessageLogsIcon width={width} height={height} />;
  } else if (type === ECalculationLogType.SUCCESS) {
    return <SuccessIcon width={width} height={height} />;
  } else if (type === ECalculationLogType.WARN) {
    return <WarningLogsIcon width={width} height={height} />;
  }
};

export const findParameterByCode = (template, paramCode) => {
  // Check if template is defined, if not return null
  if (!template || !template.sections || !Array.isArray(template.sections)) {
    return null;
  }

  function searchSubSection(subSections, paramCode) {
    for (let subSection of subSections) {
      for (let parameter of subSection.parameters) {
        if (parameter.code === paramCode) {
          return parameter;
        }
      }
      if (subSection.subSections && subSection.subSections.length > 0) {
        const result = searchSubSection(subSection.subSections, paramCode);
        if (result) return result;
      }
    }
    return null;
  }

  for (let section of template.sections) {
    const result = searchSubSection(section.subSections, paramCode);
    if (result) return result;
  }

  return null;
};

export const findDefaultOption = (parameter) => {
  if (parameter.valuesConfigs && parameter.valuesConfigs.length > 0) {
    const defaultOption = parameter.valuesConfigs.find(
      (option) => option.isDefault === true
    );
    return defaultOption || null;
  }
  return null;
};

export const _renderIcon = (modalType: ModalType) => {
  if (modalType === ModalType.DELETE) {
    return <DeleteModalIcon />;
  }
  if (modalType === ModalType.DUPLICATE) {
    return <DuplicateModalIcon />;
  }
  if (modalType === ModalType.EDIT) {
    return <EditModalIcon />;
  }
  if (modalType === ModalType.ADD) {
    return <AddModalIcon />;
  }
  if (modalType === ModalType.WARNING) {
    return <WarningModalIcon />;
  }
  if (modalType === ModalType.SUCCESS) {
    return (
      <TaskAltRoundedIcon style={{ fontSize: "34px", color: "#344054" }} />
    );
  }
};

export const _renderTitle = (modalType: ModalType) => {
  if (modalType === ModalType.DELETE) {
    return t("modal.deleteModalTitle");
  }
  if (modalType === ModalType.DUPLICATE) {
    return t("modal.duplicateModalTitle");
  }
  if (modalType === ModalType.EDIT) {
    return t("modal.editModalTitle");
  }
  if (modalType === ModalType.ADD) {
    return t("modal.addModalTitle");
  }
  if (modalType === ModalType.WARNING) {
    return t("modal.warningModalTitle");
  }
};

export const _renderSubTitle = (modalType: ModalType) => {
  if (modalType === ModalType.DELETE) {
    return t("modal.deleteModalSubTitle");
  }
  if (modalType === ModalType.DUPLICATE) {
    return t("modal.duplicateModalSubTitle");
  }
  if (modalType === ModalType.EDIT) {
    return t("modal.editModalSubTitle");
  }
  if (modalType === ModalType.ADD) {
    return t("modal.addModalSubTitle");
  }
  if (modalType === ModalType.WARNING) {
    return t("modal.warningModalSubTitle");
  }
};
